import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, formatApiError } from "../../lib/api";

const inputCls = "w-full bg-[#0D1117] border border-[#263342] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-crimson transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2";

export default function ServicesManager() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", price: "", desc: "" });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/services");
      setServices(data);
    } catch (err) {
      toast.error("Could not load services", { description: formatApiError(err) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, price: s.price, desc: s.desc });
  };

  const openAdd = () => {
    setAdding(true);
    setForm({ name: "", price: "", desc: "" });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (adding) {
        await api.post("/services", form);
        toast.success("Service added");
        setAdding(false);
      } else {
        await api.put(`/services/${editing.id}`, form);
        toast.success("Service updated");
        setEditing(null);
      }
      load();
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/services/${id}`);
      toast.success("Service removed");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    }
  };

  const dialogOpen = !!editing || adding;

  return (
    <div className="mt-12" data-testid="services-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold uppercase tracking-tight text-2xl text-slate-100">Service Menu &amp; Pricing</h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">Changes go live on the site instantly</p>
        </div>
        <button
          data-testid="service-add-button"
          onClick={openAdd}
          className="flex items-center gap-2 bg-crimson text-white font-display font-semibold uppercase tracking-widest text-sm px-5 py-2.5 hover:-translate-y-0.5 transition-transform"
        >
          <Plus size={16} /> Add Service
        </button>
      </div>

      <div className="bg-[#0D1117] border border-[#263342] divide-y divide-[#263342]/60" data-testid="services-manager-list">
        {services.map((s, i) => (
          <div key={s.id} className="flex flex-wrap items-center gap-4 px-5 py-4" data-testid={`service-admin-row-${i}`}>
            <span className="font-mono text-xs text-slate-500 w-16">{s.code}</span>
            <span className="flex-1 min-w-[180px] font-semibold text-slate-100 text-sm">{s.name}</span>
            <span className="font-mono text-xs text-crimson uppercase tracking-[0.1em]" data-testid={`service-admin-price-${i}`}>{s.price}</span>
            <button
              data-testid={`service-edit-${i}`}
              onClick={() => openEdit(s)}
              className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] border border-[#263342] px-3 py-2 text-slate-300 hover:border-crimson hover:text-crimson transition-colors"
            >
              <Pencil size={12} /> Edit
            </button>
            {confirmDelete === s.id ? (
              <button
                data-testid={`service-delete-confirm-${i}`}
                onClick={() => remove(s.id)}
                className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] border border-red-500 bg-red-500/10 px-3 py-2 text-red-400"
              >
                <Trash2 size={12} /> Confirm?
              </button>
            ) : (
              <button
                data-testid={`service-delete-${i}`}
                onClick={() => setConfirmDelete(s.id)}
                className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] border border-[#263342] px-3 py-2 text-slate-400 hover:border-red-500 hover:text-red-400 transition-colors"
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={() => { setEditing(null); setAdding(false); }}>
        <DialogContent className="bg-panel border-[#263342] text-slate-100 max-w-lg" data-testid="service-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold uppercase tracking-tight text-2xl">
              {adding ? "Add Service" : "Edit Service"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="mt-2 space-y-4" data-testid="service-form">
            <div>
              <label className={labelCls}>Name</label>
              <input required className={inputCls} data-testid="service-form-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Price label</label>
              <input required className={inputCls} data-testid="service-form-price" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} placeholder="from $199" />
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea rows={3} className={inputCls} data-testid="service-form-desc" value={form.desc} onChange={(e) => setForm((f) => ({ ...f, desc: e.target.value }))} />
            </div>
            <button type="submit" data-testid="service-form-save" disabled={saving} className="w-full bg-crimson text-white font-display font-semibold uppercase tracking-widest py-3.5 disabled:opacity-60">
              {saving ? "Saving..." : adding ? "Add Service" : "Save Changes"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
